// SunCalc-inspired offline calculator (no external dependencies)
// This provides accurate calculations that can work offline for years
const SunCalculator = {
  // Convert degrees to radians
  toRadians: function(degrees) {
    return degrees * Math.PI / 180;
  },

  // Convert radians to degrees
  toDegrees: function(radians) {
    return radians * 180 / Math.PI;
  },

  // Calculate day of year
  dayOfYear: function(date) {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date - start;
    return Math.floor(diff / 86400000);
  },

  // Calculate Julian date
  getJulianDate: function(date) {
    return (date.getTime() / 86400000.0) + 2440587.5;
  },

  // Calculate the position of the sun 
  getSunPosition: function(date, lat, lng) {
    const julianDay = this.getJulianDate(date);
    const t = (julianDay - 2451545.0) / 36525.0;
    
    // Mean longitude of the sun
    let L0 = 280.46646 + 36000.76983 * t + 0.0003032 * t * t;
    L0 = L0 % 360;
    
    // Mean anomaly of the sun
    let M = 357.52911 + 35999.05029 * t - 0.0001537 * t * t;
    M = M % 360;
    
    // Equation of center
    const C = (1.914602 - 0.004817 * t - 0.000014 * t * t) * Math.sin(this.toRadians(M)) +
              (0.019993 - 0.000101 * t) * Math.sin(this.toRadians(2 * M)) +
              0.000289 * Math.sin(this.toRadians(3 * M));
    
    // True longitude of the sun
    const sunLong = L0 + C;
    
    // Obliquity of the ecliptic
    const obliqCorr = 23.4398 - 0.013 * t;
    
    // Right ascension and declination
    const ra = this.toDegrees(Math.atan2(
      Math.cos(this.toRadians(obliqCorr)) * Math.sin(this.toRadians(sunLong)),
      Math.cos(this.toRadians(sunLong))
    ));
    
    const dec = this.toDegrees(Math.asin(
      Math.sin(this.toRadians(obliqCorr)) * Math.sin(this.toRadians(sunLong))
    ));
    
    return { ra, dec };
  },

  // Calculate times for sunrise/sunset
  calculateTimes: function(date, lat, lng) {
    // Constants
    const zenith = 90.83; // Official sunrise/sunset zenith
    const latitude = lat;
    const longitude = lng;
    
    // Removed hardcoded Sydney times to use calculation for accuracy

    // Get sun position
    const sunPos = this.getSunPosition(date, lat, lng);
    const solarDec = sunPos.dec;
    
    const cosZenith = Math.cos(this.toRadians(zenith));
    const cosLatitude = Math.cos(this.toRadians(latitude));
    const sinLatitude = Math.sin(this.toRadians(latitude));
    const cosDec = Math.cos(this.toRadians(solarDec));
    const sinDec = Math.sin(this.toRadians(solarDec));
    
    // Calculate hour angle
    const cosHourAngle = (cosZenith - sinDec * sinLatitude) / (cosDec * cosLatitude);
    
    // Handle edge cases (polar day/night)
    if (cosHourAngle > 1) {
      // Sun never rises
      return {
        sunrise: null,
        sunset: null,
        polarNight: true,
        polarDay: false
      };
    } else if (cosHourAngle < -1) {
      // Sun never sets
      return {
        sunrise: null,
        sunset: null,
        polarNight: false,
        polarDay: true
      };
    }
    
    // Calculate local hour angles
    const hourAngleRad = Math.acos(cosHourAngle);
    const hourAngleDeg = this.toDegrees(hourAngleRad);

    // Equation of Time correction (in minutes)
    const B = this.toRadians(360 / 365 * (this.dayOfYear(date) - 81));
    const EqT = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);

    // Calculate local mean time for rising and setting (in UTC minutes)
    const localMeanTimeSunrise = 720 - 4 * (longitude + hourAngleDeg) - EqT;
    const localMeanTimeSunset = 720 - 4 * (longitude - hourAngleDeg) - EqT;

    // Normalize to 0-1440 range
    const utcSunriseMinutes = ((localMeanTimeSunrise % 1440) + 1440) % 1440;
    const utcSunsetMinutes = ((localMeanTimeSunset % 1440) + 1440) % 1440;

    // Create date objects in UTC — the algorithm already produces correct
    // UTC minutes, so NO timezone adjustment is needed.  getHours() will
    // return machine-local time, and callers needing location-local time
    // should use Intl.DateTimeFormat with the IANA timezone or estimate
    // the offset from longitude.
    const sunrise = new Date(date);
    sunrise.setUTCHours(0, 0, 0, 0);
    sunrise.setUTCMinutes(utcSunriseMinutes);

    const sunset = new Date(date);
    sunset.setUTCHours(0, 0, 0, 0);
    sunset.setUTCMinutes(utcSunsetMinutes);

    return {
      sunrise: sunrise,
      sunset: sunset,
      polarNight: false,
      polarDay: false
    };
  }
};

export default SunCalculator;